import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Droppable } from 'react-beautiful-dnd';
import { PILLAR_ICON_FOR_KEY } from '../../Program/components/learning-lifecycle/pillarIcons';
import type { LearningPillar } from '../../Program/components/learning-lifecycle/types';
import { MOBILE_SX } from '../../../../../mobile/mobileApp';

interface Props {
  pillar: LearningPillar;
  programCount: number;
  children?: React.ReactNode; // program cards land here in Step 4
  /** Set to make the card list a drop zone for reordering within this pillar. */
  droppableId?: string;
  /** Phone: the header is a tab that opens and closes the cards. */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

/**
 * Member pillar column — read-only mirror of Admin's PillarColumn read-only
 * header. No edit mode, no Add Program CTA, no Remove CTA, no DnD wrapper —
 * the Member view is strictly read-only per PRD §"What Members Cannot Do".
 *
 * Header styling intentionally matches Admin's ReadOnlyPillarHeader so the
 * two views feel like the same surface from the user's perspective.
 */
const MemberPillarColumn: React.FC<Props> = ({
  pillar,
  programCount,
  children,
  droppableId,
  collapsed = false,
  onToggleCollapsed
}) => {
  const Icon = pillar.icon ? PILLAR_ICON_FOR_KEY[pillar.icon] : null;

  return (
    // Column wrapper — Figma: each column is 25% of the viewport so 4 fit
    // across at any time. A 5th overflows right, triggering the nav arrow.
    // bg #1C1C1C, right border #2A2A2A, padding 20, gap 10.
    <Stack
      sx={{
        flex: '0 0 25%',
        minWidth: 0,
        height: '100%',
        // Symmetric padding — the Stack gap (header→body) matches the
        // bottom padding (card3→column bg bottom) at 16px each so the
        // top and bottom rest evenly.
        // Tight horizontal padding so pillars sit closer and the tier panel
        // beside them can take the width.
        // No top padding, so the pillar header boxes line up with the top
        // outline of the Development Tier panel beside them.
        pt: 0,
        pb: '16px',
        px: '8px',
        gap: '16px',
        // Transparent so the near-black page shows through behind the cards —
        // the old #1C1C1C fill sat almost on top of the #151515 tiles and their
        // #2A2A2A borders, flattening the column into one block. On the darker
        // page the card borders read and each tile stands alone. Matches the
        // admin Learning Framework dashboard.
        bgcolor: 'transparent',
        borderRight: '1px solid #2A2A2A',
        // Phone: a section of the one-column list, as tall as its cards.
        [MOBILE_SX]: { flex: 'none', height: 'auto', px: 0, pb: 0, gap: '10px', borderRight: 'none' }
      }}
    >
      {/* Read-only pillar header — Figma:
            padding 16/20, radius 4, border 1px #868686
            (Stroke-Border-border-highlight), gap 8; icon 24x24 (or placeholder
            circle when null); title Inter 18/600 uppercase #FDFDFD;
            count pill bg #2A2A2A radius 2, text Inter 16/400 #868686. */}
      <Box
        {...((onToggleCollapsed
          ? {
              component: 'button',
              type: 'button',
              onClick: onToggleCollapsed,
              'aria-expanded': !collapsed,
              'data-testid': 'stage-tab'
            }
          : {}) as object)}
        sx={{
          // Slimmer, with the brandy frame + faint tint used on the side panels.
          border: '1px solid rgba(247, 148, 32, 0.45)',
          bgcolor: 'rgba(247, 148, 32, 0.06)',
          borderRadius: '4px',
          pl: '16px',
          pr: '16px',
          pt: '5px',
          pb: '5px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          // Phone: a full-width tab, as on the admin's board, tall enough to tap, with room for its chevron.
          [MOBILE_SX]: { position: 'relative', width: '100%', minHeight: 48, pl: '40px', pr: '40px', cursor: 'pointer' }
        }}
      >
        <Box sx={{ width: 24, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          {Icon ? (
            <Icon size={24} color="#FDFDFD" />
          ) : (
            <Box sx={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #FDFDFD' }} />
          )}
        </Box>
        <Typography
          sx={{
            color: '#FDFDFD',
            fontFamily: 'Inter, sans-serif',
            fontSize: 18,
            fontWeight: 600,
            textTransform: 'uppercase'
          }}
        >
          {pillar.name}
        </Typography>
        <Box
          sx={{
            bgcolor: '#2A2A2A',
            borderRadius: '2px',
            pl: '6px',
            pr: '6px',
            pt: '2px',
            pb: '2px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography
            sx={{
              color: '#868686',
              fontFamily: 'Inter, sans-serif',
              fontSize: 16,
              fontWeight: 400,
              lineHeight: 1
            }}
          >
            {programCount}
          </Typography>
        </Box>
        {onToggleCollapsed && (
          <ExpandMoreIcon
            sx={{
              position: 'absolute',
              top: '50%',
              right: '10px',
              mt: '-11px',
              fontSize: 22,
              color: '#B6B6B6',
              transform: collapsed ? 'none' : 'rotate(180deg)',
              transition: 'transform 0.15s'
            }}
          />
        )}
      </Box>

      {/* Body — program cards stack here. Inter-card gap matches the
          column's symmetric padding for a consistent visual rhythm.
          flex:1 fills the remaining column height; justify-content:center
          then splits any slack equally above card 1 and below the last
          card, so the gap between header-bottom and card-1-top always
          equals the gap between last-card-bottom and column-bottom —
          regardless of viewport height. No Droppable wrapper: Member
          view has no DnD. */}
      {!collapsed && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            overflowY: 'auto',
            [MOBILE_SX]: { flex: 'none', overflowY: 'visible', gap: '10px' }
          }}
        >
          {/* Learners can reorder cards within this pillar (never across) — the
              dashboard's DragDropContext rejects drops into another column. */}
          {droppableId ? (
            <Droppable droppableId={droppableId} ignoreContainerClipping>
              {(provided, snapshot) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    flex: '1 0 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    borderRadius: '6px',
                    bgcolor: snapshot.isDraggingOver ? 'rgba(247, 148, 32, 0.04)' : 'transparent',
                    transition: 'background-color 0.15s',
                    [MOBILE_SX]: { gap: '10px' }
                  }}
                >
                  {children}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          ) : (
            children
          )}
        </Box>
      )}
    </Stack>
  );
};

export default MemberPillarColumn;
